import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  SwapVert as SortIcon,
  CallMerge as DeduplicateIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { editeursAPI } from '../services/api';

const getInitials = (nom) => {
  const words = nom.split(' ');
  if (words.length >= 2) {
    return `${words[0].charAt(0).toUpperCase()}${words[words.length - 1].charAt(0).toUpperCase()}`;
  }
  return nom.charAt(0).toUpperCase();
};

function EditeursPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortType, setSortType] = useState('nom-asc'); // 'nom-asc', 'nom-desc', 'livres-desc', 'livres-asc'
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, editeur: null });
  
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Récupérer les éditeurs
  const { data, isLoading, error } = useQuery(
    ['editeurs', page, search, sortType],
    () => editeursAPI.getAll({ page, limit: 12, search, sort: sortType }).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  // Mutation pour supprimer un éditeur
  const deleteEditeurMutation = useMutation(
    (id) => editeursAPI.delete(id),
    {
      onSuccess: () => {
        toast.success(t('editeursPage.deleteSuccess'));
        queryClient.invalidateQueries('editeurs');
        setDeleteDialog({ open: false, editeur: null });
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(t('editeursPage.deleteError'));
        } else {
          toast.error(t('editeursPage.deleteGenericError'));
        }
      }
    }
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortSelect = (sortValue) => {
    setSortType(sortValue);
    handleSortMenuClose();
  };

  const getSortLabel = () => {
    switch (sortType) {
      case 'nom-asc':
        return t('editeursPage.sort.nameAsc');
      case 'nom-desc':
        return t('editeursPage.sort.nameDesc');
      case 'livres-desc':
        return t('editeursPage.sort.booksDesc');
      case 'livres-asc':
        return t('editeursPage.sort.booksAsc');
      default:
        return t('editeursPage.sort.nameAsc');
    }
  };

  const handleDeduplicate = () => {
    navigate('/editeurs/doublons');
  };

  const handleAdd = () => {
    navigate('/editeurs/ajouter');
  };

  const handleEdit = (editeur) => {
    navigate(`/editeurs/${editeur.id}/modifier`);
  };

  const handleDelete = (editeur) => {
    setDeleteDialog({ open: true, editeur });
  };

  const confirmDelete = () => {
    if (deleteDialog.editeur) {
      deleteEditeurMutation.mutate(deleteDialog.editeur.id);
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        {t('editeursPage.loadingError', { message: error.message })}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Barre de recherche et boutons */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, mb: 3, alignItems: 'center', flexWrap: { xs: 'nowrap', sm: 'nowrap' } }}>
        <TextField
          fullWidth
          placeholder={t('editeursPage.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
        <Button
          variant="outlined"
          startIcon={<SortIcon />}
          onClick={handleSortMenuOpen}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            backgroundColor: 'background.default',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {getSortLabel()}
          </Box>
        </Button>
        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={handleSortMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => handleSortSelect('nom-asc')}
            selected={sortType === 'nom-asc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'nom-asc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'nom-asc' && <Box sx={{ width: '26px' }} />}
              {t('editeursPage.sort.nameAsc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('nom-desc')}
            selected={sortType === 'nom-desc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'nom-desc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'nom-desc' && <Box sx={{ width: '26px' }} />}
              {t('editeursPage.sort.nameDesc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('livres-desc')}
            selected={sortType === 'livres-desc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'livres-desc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'livres-desc' && <Box sx={{ width: '26px' }} />}
              {t('editeursPage.sort.booksDesc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('livres-asc')}
            selected={sortType === 'livres-asc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'livres-asc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'livres-asc' && <Box sx={{ width: '26px' }} />}
              {t('editeursPage.sort.booksAsc')}
            </Box>
          </MenuItem>
        </Menu>
        <Button
          variant="outlined"
          startIcon={<DeduplicateIcon />}
          onClick={handleDeduplicate}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            backgroundColor: 'background.default',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('editeursPage.duplicates')}
          </Box>
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('editeursPage.add')}
          </Box>
        </Button>
      </Box>

      {/* Résultats */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {data?.data?.length === 0 ? (
            <Alert severity="info">
              {search ? t('editeursPage.noResults') : t('editeursPage.empty')}
            </Alert>
          ) : (
            <Card sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '18px',
                    mb: 0.5,
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                  >
                    {t('editeursPage.title')}
                  </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {data?.pagination?.total === 1 
                    ? t('editeursPage.count', { count: data?.pagination?.total || 0 })
                    : t('editeursPage.countPlural', { count: data?.pagination?.total || 0 })}
                </Typography>
              </Box>
              <Divider />
              <List sx={{ py: 0 }}>
                {data?.data?.map((editeur, index) => (
                  <React.Fragment key={editeur.id}>
                    <ListItem
                      button
                      onClick={() => navigate(`/editeurs/${editeur.id}`)}
                      sx={{
                        py: 2,
                        px: 3,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getInitials(editeur.nom)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                            }}
                          >
                            {editeur.nom}
                          </Typography>
                        }
                        secondary={
                          editeur.adresse && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                              }}
                            >
                              {editeur.adresse}
                            </Typography>
                          )
                        }
                      />
                      <Box sx={{ textAlign: 'right', ml: 2 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 400,
                            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          {editeur.nombre_livres || 0}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          {editeur.nombre_livres === 1 
                            ? t('editeursPage.bookCount', { count: editeur.nombre_livres || 0 })
                            : t('editeursPage.bookCountPlural', { count: editeur.nombre_livres || 0 })}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < data.data.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              {data?.pagination?.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, pt: 2 }}>
                  <Pagination
                    count={data.pagination.totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </Card>
          )}
        </>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, editeur: null })}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('editeursPage.confirmDeleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('editeursPage.confirmDeleteMessage', { name: deleteDialog.editeur?.nom })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, editeur: null })}>
            {t('editeursPage.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteEditeurMutation.isLoading || deleteDialog.editeur?.nombre_livres > 0}
          >
            {deleteEditeurMutation.isLoading ? t('editeursPage.deleting') : t('editeursPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EditeursPage;
